import React, { useState, useRef, useEffect } from 'react';

import { DiffContent } from '@atlassian/bitkit-diff';

import { CodemirrorDiffEditorLite } from './codemirror-diff-editor-lite';
import { CODE_SUGGESTIONS_EXTENSION_TYPE } from './constants';
import { getDiffContentWithoutPrefix, getDiffStartLineNum } from './utils';

interface CodeSuggestionEditorProps {
  diffLine: DiffContent;
  nodeLocalId: string;
  saveSuggestion: (suggestion: Record<string, string>) => void;
  suggestionText?: string;
}

export const CodeSuggestionEditor = React.memo(function CodeSuggestionEditor({
  diffLine,
  nodeLocalId,
  saveSuggestion,
  suggestionText,
}: CodeSuggestionEditorProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const originalCode = getDiffContentWithoutPrefix(diffLine);

  const initialSuggestion = suggestionText ?? originalCode;
  const [currentSuggestion, setCurrentSuggestion] = useState(initialSuggestion);
  const [isNodeSelected, setIsNodeSelected] = useState(false);
  const [commentEditorId, setCommentEditorId] = useState<string | null>(null);
  const [commentEditorElement, setCommentEditorElement] =
    useState<HTMLElement | null>(null);

  // Set editor element and ID once mounted
  useEffect(() => {
    const tryGetEditorElement = () => {
      const el = document.activeElement?.closest(
        '#ak-editor-textarea'
      ) as HTMLElement | null;
      if (el) {
        setCommentEditorElement(el);
        const id = el.getAttribute('data-editor-id');
        if (id) {
          setCommentEditorId(id);
        }
      } else {
        // Retry in case the editor hasn't rendered yet
        setTimeout(tryGetEditorElement, 100);
      }
    };

    tryGetEditorElement();
  }, []);

  useEffect(() => {
    saveSuggestion({ [nodeLocalId]: currentSuggestion });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent editing in outer container
  useEffect(() => {
    if (!commentEditorElement) return undefined;

    const observer = new MutationObserver(() => {
      const elements = commentEditorElement.querySelectorAll(
        `div[extensionType='${CODE_SUGGESTIONS_EXTENSION_TYPE}']`
      );
      elements.forEach(element => {
        element.setAttribute('contenteditable', 'false');
        if (element.getAttribute('localId') === nodeLocalId) {
          const isSelected = element.classList.contains(
            'ak-editor-selected-node'
          );
          setIsNodeSelected(isSelected);
        }
      });
    });

    observer.observe(commentEditorElement, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [nodeLocalId, commentEditorElement]);

  const handleSuggestionChange = (newSuggestion: string) => {
    setCurrentSuggestion(newSuggestion);
    saveSuggestion({ [nodeLocalId]: newSuggestion });
  };

  return (
    <div ref={wrapperRef}>
      {commentEditorId && (
        <CodemirrorDiffEditorLite
          startLineNum={getDiffStartLineNum(diffLine)}
          originalCode={originalCode}
          updatedCode={currentSuggestion}
          onChange={handleSuggestionChange}
          nodeLocalId={nodeLocalId}
          disableCMCursor={isNodeSelected}
          parentEditorId={commentEditorId}
        />
      )}
    </div>
  );
});
